/* eslint-disable no-param-reassign */
import { useEffect } from 'react';
import { DropdownItem } from '../components/SearchableDropdown/SearchableDropdown';

interface UseDropdownEventsProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  setPage: (page: number) => void;
  popoverRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLDivElement>;
  searchInputRef: React.RefObject<HTMLInputElement>;
  transitionRef: React.MutableRefObject<boolean>;
  lastCloseTimeRef: React.MutableRefObject<number>;
  resetState: () => void;
  onSearch: (query: string, page: number) => void;
  onSelect: (item: DropdownItem) => void;
  onClose?: () => void;
  onClear?: () => void;
  onLoadMore?: () => void;
  onCustomValueCreate?: (value: string) => void;
  loading?: boolean;
  hasMore?: boolean;
  allowCustomValue?: boolean;
  floatingMode?: boolean;
}

export default function useDropdownEvents({
  isOpen,
  setIsOpen,
  search,
  setSearch,
  page,
  setPage,
  popoverRef,
  triggerRef,
  searchInputRef,
  transitionRef,
  lastCloseTimeRef,
  resetState,
  onSearch,
  onSelect,
  onClose,
  onClear,
  onLoadMore,
  onCustomValueCreate,
  loading = false,
  hasMore = false,
  allowCustomValue = false,
  floatingMode = false
}: UseDropdownEventsProps) {
  const handleTogglePopover = (): void => {
    if (transitionRef.current) return;

    if (!isOpen) {
      const timeSinceClose = Date.now() - lastCloseTimeRef.current;
      if (timeSinceClose < 50) return;

      transitionRef.current = true;

      requestAnimationFrame(() => {
        resetState();
        onSearch('', 0);
        setIsOpen(true);
        transitionRef.current = false;

        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      });
    } else {
      transitionRef.current = true;
      lastCloseTimeRef.current = Date.now();
      setIsOpen(false);
      onClose?.();

      setTimeout(() => {
        transitionRef.current = false;
      }, 100);
    }
  };

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (!loading && hasMore && scrollTop + clientHeight >= scrollHeight - 10) {
      const nextPage = page + 1;
      setPage(nextPage);
      onLoadMore?.();
    }
  };

  const handleSelect = (item: DropdownItem): void => {
    onSelect(item);

    transitionRef.current = true;
    lastCloseTimeRef.current = Date.now();
    setIsOpen(false);
    onClose?.();

    setTimeout(() => {
      transitionRef.current = false;
    }, 100);
  };

  const handleClear = (): void => {
    onClear?.();
    setSearch('');
    setPage(0);
  };

  const handleCreateCustomValue = (): void => {
    if (!allowCustomValue || !onCustomValueCreate || !search.trim()) return;

    const customValue = search.trim();
    onCustomValueCreate(customValue);

    transitionRef.current = true;
    lastCloseTimeRef.current = Date.now();
    setIsOpen(false);
    onClose?.();
    setSearch('');

    setTimeout(() => {
      transitionRef.current = false;
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const shouldShowAddNew = allowCustomValue && search.trim() !== '';

    if (e.key === 'Enter' && shouldShowAddNew) {
      e.preventDefault();
      handleCreateCustomValue();
    }
  };

  useEffect(() => {
    if (floatingMode && !isOpen) {
      setIsOpen(true);
      resetState();
      onSearch('', 0);

      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [floatingMode, isOpen, onSearch, resetState, setIsOpen, searchInputRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        isOpen &&
        popoverRef.current &&
        !triggerRef.current?.contains(event.target as Node) &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        transitionRef.current = true;
        lastCloseTimeRef.current = Date.now();
        setIsOpen(false);
        onClose?.();

        setTimeout(() => {
          transitionRef.current = false;
        }, 100);
      }
    };

    const handleWheel = (event: WheelEvent): void => {
      if (isOpen && popoverRef.current?.contains(event.target as Node)) {
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('wheel', handleWheel, {
      passive: false,
      capture: true
    });

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('wheel', handleWheel, true);
    };
  }, [
    isOpen,
    onClose,
    popoverRef,
    triggerRef,
    transitionRef,
    lastCloseTimeRef,
    setIsOpen
  ]);

  return {
    handleTogglePopover,
    handleListScroll,
    handleSelect,
    handleClear,
    handleCreateCustomValue,
    handleKeyDown
  };
}
